import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, RefreshCw, Minus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useChartTheme } from "@/lib/chartTheme";

const relTime = (iso) => {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 129600) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

function Spark({ history, up }) {
  const pts = (history || []).map((h) => h.price).filter((v) => v != null);
  if (pts.length < 2) return <div className="h-8" />;
  const min = Math.min(...pts), max = Math.max(...pts), rng = (max - min) || 1;
  const W = 140, H = 34;
  const d = pts.map((v, i) => `${((i / (pts.length - 1)) * W).toFixed(1)},${(H - ((v - min) / rng) * H).toFixed(1)}`).join(" ");
  return (
    <svg width={W} height={H} className="overflow-visible">
      <polyline points={d} fill="none" stroke={up ? "#059669" : "#e11d48"} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

export default function CommodityTracker({ canEdit }) {
  const [idx, setIdx] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [catLines, setCatLines] = useState([]);
  const [priceDraft, setPriceDraft] = useState("");
  const ct = useChartTheme();
  const load = () => api.get("/commodity-indices").then((r) => setIdx(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { setPriceDraft(""); }, [detail?.index_id]);
  useEffect(() => { api.get("/catalog?type=line_item&limit=500").then((r) => setCatLines(r.data?.items || (Array.isArray(r.data) ? r.data : []))).catch(() => {}); }, []);
  const saveManual = async () => {
    const v = Number(priceDraft);
    if (!(v > 0)) { toast.error("Enter a price greater than 0"); return; }
    setBusy(true);
    try {
      await api.put(`/commodity-indices/${detail.index_id}/price`, { current_price: v });
      toast.success("Price updated");
      setPriceDraft("");
      setDetail((d) => ({ ...d, current_price: v }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  const refresh = async () => {
    setBusy(true);
    try { const r = await api.post("/commodity-indices/refresh"); toast.success(`Prices refreshed — ${r.data.updated} updated${r.data.failed ? `, ${r.data.failed} unavailable` : ""}`); load(); }
    catch { toast.error("Could not refresh prices"); }
    finally { setBusy(false); }
  };
  const lastUpd = idx.map((x) => x.updated_at).filter(Boolean).sort().slice(-1)[0];

  return (
    <div className="space-y-4" data-testid="commodity-tracker">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-slate-500 flex-1">Commodity prices that drive index-linked material costs. <b>Live</b> metals &amp; energy refresh automatically every morning (6am ET) from Yahoo Finance; <b>manual</b> construction commodities (concrete, conduit, wire…) are owner-updated — open one to set its price. Link a catalog item to an index and its baseline material price auto-adjusts as the market moves, but any bid that's already been priced stays frozen. Only a <b>revision</b> can be repriced to current rates.</p>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[11px] text-slate-400">Updated {relTime(lastUpd)}</span>
          {canEdit && <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="commodity-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh prices</Button>}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {idx.map((x) => {
          const chg = Number(x.change_pct) || 0;
          const up = chg > 0, down = chg < 0;
          const Tone = up ? TrendingUp : down ? TrendingDown : Minus;
          const tone = up ? "text-emerald-600" : down ? "text-rose-600" : "text-slate-400";
          return (
            <Card key={x.index_id} onClick={() => setDetail(x)} className="p-4 cursor-pointer hover:border-[#003E87]/40 hover:shadow-sm transition-all" data-testid={`commodity-card-${x.index_id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{x.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">{x.group || x.symbol}</div>
                </div>
                <span className={`inline-flex items-center gap-1 text-xs font-medium ${tone}`}><Tone className="w-3.5 h-3.5" /> {up ? "+" : ""}{chg.toFixed(2)}%</span>
              </div>
              <div className="mt-2 flex items-end justify-between">
                <div>
                  <div className="text-lg font-mono font-semibold text-slate-900" data-testid={`commodity-price-${x.index_id}`}>
                    {x.current_price != null ? `$${Number(x.current_price).toLocaleString("en-US", { maximumFractionDigits: 4 })}` : "—"}
                  </div>
                  <div className="text-[10px] text-slate-400">{x.unit}{x.stale ? " · stale" : ""}</div>
                </div>
                <Spark history={x.history} up={!down} />
              </div>
            </Card>
          );
        })}
      </div>
      {idx.length === 0 && <Card className="p-8 text-center text-sm text-slate-400">No index data yet. {canEdit ? "Click Refresh prices to pull the latest." : ""}</Card>}

      {detail && (
        <Dialog open onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="max-w-2xl" data-testid="commodity-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">{detail.name} <span className="text-xs font-normal text-slate-400">{detail.manual ? "manual index" : detail.symbol} · {detail.unit}</span></DialogTitle>
              <DialogDescription>
                {detail.current_price != null ? `$${Number(detail.current_price).toLocaleString("en-US", { maximumFractionDigits: 4 })} ${detail.unit}` : "—"}
                <span className={`ml-2 ${(Number(detail.change_pct) || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{(Number(detail.change_pct) || 0) >= 0 ? "+" : ""}{(Number(detail.change_pct) || 0).toFixed(2)}%</span>
                {" · "}{detail.manual ? "Owner-set price history" : "Price trend from Yahoo Finance (last ~3 months)"}
              </DialogDescription>
            </DialogHeader>
            {canEdit && detail.manual && (
              <div className="flex items-end gap-2 border border-amber-200 bg-amber-50 rounded-lg p-3" data-testid="commodity-manual-editor">
                <label className="flex-1 text-[11px] text-amber-800">
                  Manual index — set the current price ({detail.unit})
                  <input type="number" step="any" value={priceDraft} onChange={(e) => setPriceDraft(e.target.value)} placeholder={detail.current_price ?? "0"}
                    className="w-full mt-1 text-sm border border-amber-300 rounded px-2 py-1.5 font-mono outline-none focus:border-amber-500" data-testid="commodity-manual-price-input" />
                </label>
                <Button size="sm" disabled={busy} onClick={saveManual} className="bg-[#003E87] hover:bg-[#00306a]" data-testid="commodity-manual-save">Save price</Button>
              </div>
            )}
            <div className="h-56" data-testid="commodity-detail-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={(detail.history || []).map((h) => ({ date: h.date, price: h.price }))} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: ct.tickDim }} minTickGap={40} stroke={ct.axis} />
                  <YAxis domain={["auto", "auto"]} tick={{ fontSize: 10, fill: ct.tickDim }} width={54} stroke={ct.axis} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v) => [`$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 4 })}`, detail.name]} labelStyle={{ ...ct.labelStyle, fontSize: 11 }} contentStyle={ct.contentStyle} itemStyle={ct.itemStyle} />
                  <Line type="monotone" dataKey="price" stroke={ct.line} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Catalog items driven by this index</div>
              {(() => {
                const linked = (catLines || []).filter((c) => c.commodity_index_id === detail.index_id);
                if (linked.length === 0) return <p className="text-xs text-slate-400">No catalog line items are linked to {detail.name} yet. Link one from the Catalog tab (or bulk-link several).</p>;
                return (
                  <div className="max-h-40 overflow-auto divide-y divide-slate-50 border border-slate-100 rounded" data-testid="commodity-linked-items">
                    {linked.map((c) => (
                      <div key={c.catalog_id} className="flex items-center justify-between px-2 py-1.5 text-xs">
                        <span className="truncate text-slate-700">{c.name}{c.cost_code ? <span className="ml-1 text-[10px] font-mono text-[#003E87]">[{c.cost_code}]</span> : null}</span>
                        <span className="font-mono text-slate-500 shrink-0">${Number(c.material_adjusted ?? c.unit_material_cost ?? 0).toLocaleString("en-US", { maximumFractionDigits: 4 })}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
